<script lang="ts">
    import { Component, Prop, mixins, Watch } from 'vue-facing-decorator';
    import { CurrencyHandler, IconName, PromiseHelper, TranslationKey, PerformanceQASelectors } from "webshop-modules";
    import IconComponent from "@/components/atoms/Icon.vue";
    import {
        panelStateService, SelectableInfo,
        StructuredPricelevel,
        TicketSelectionApplicationData, TicketTypeItem,
        useSeatmapStore
    } from "@/domain/ticket-selection";
    import VueMultiselect from "vue-multiselect/src/Multiselect.vue";
    import { BreakpointHandler, TicketSelectionDisplay } from "@/mixins";
    import ButtonComponent from "@/components/atoms/button/Button.vue";
    import { TranslationPlugin } from "@/plugins/translation.plugin";
    import PromotionPanel from "@/components/ticket-selection/seatmap/PromotionPanel.vue";
    import TicketFeeInfo from "@/components/atoms/ticket-fee/TicketFeeInfo.vue";

    @Component({
        name: 'SelectionComponent',
        components: {
            TicketFeeInfo,
            PromotionPanel,
            ButtonComponent,
            IconComponent,
            VueMultiselect,
        }
    })
    export default class SelectionComponent extends mixins(BreakpointHandler, TicketSelectionDisplay) {

        @Prop() public data!: TicketSelectionApplicationData;

        public readonly Translation: typeof TranslationKey = TranslationKey;
        public readonly IconName: typeof IconName = IconName;
        public readonly PerformanceQASelectors: typeof PerformanceQASelectors = PerformanceQASelectors;
        public readonly seatmapStore = useSeatmapStore();
        public readonly panelState = panelStateService;

        public currencyHandler!: CurrencyHandler;
        public openedSelection: SelectableInfo | null = null;

        private submitButtonLabel = '';

        @Watch('seatmapStore.getStructuredSelection')
        public onSelectionChange(selection: StructuredPricelevel[]) {
            if (selection.length === 0 && this.isMobile) {
                this.panelState.closePanel();
            }
        }

        public get totalTicketsSelected(): number {
            return this.seatmapStore.getStructuredSelection.length;
        }

        public get submitSelectionLabel() {
            const price = this.currencyHandler.displayPrimaryCurrency(this.seatmapStore.totalSelectionAmount);

            return TranslationPlugin.decorateWithParams(this.submitButtonLabel, {
                key: this.Translation.Ticket_selection_ticket_count_submit,
                keyValues: JSON.stringify({
                    amount: this.totalTicketsSelected,
                    price,
                }),
                identifierStart: '{',
                identifierEnd: '}'
            });
        }

        public getSelectedTicketsPriceList(): number[] {
            return this.seatmapStore.getStructuredSelection.reduce((acc: number [], selection: StructuredPricelevel) => {
                const activeTicketType = this.getActiveTicketTypeFor(selection.selectionInfo);

                if (activeTicketType) {
                    return acc.concat(activeTicketType.price);
                }

                return acc;
            }, []);
        }

        public getActiveTicketTypeFor(selectable: SelectableInfo): TicketTypeItem {
            return <TicketTypeItem>selectable.ticketTypes.find(ticketType => ticketType.active);
        }

        public setOpenedSelectionTo(selectable: SelectableInfo, pricelevelIndex?: number): void {
            if (this.openedSelection && this.openedSelection.id === selectable.id) {
                this.openedSelection = null;
            } else {
                this.openedSelection = selectable;
            }

            if (pricelevelIndex) {
                this.expandIntoViewport(`pricelevel-${pricelevelIndex}`);
            }
        }

        public isTicketOpen(ticket: SelectableInfo): boolean {
            return this.openedSelection?.id === ticket.id
                && this.openedSelection.selectionDisplayPosition === ticket.selectionDisplayPosition;
        }

        public handleTicketTypeSelection(selectable: SelectableInfo, ticketType: TicketTypeItem): void {
            this.panelState.updateSelectedTicketType(selectable, ticketType);
            this.openedSelection = null;
        }

        public removeSelection(ticket: SelectableInfo): void {
            this.panelState.removeSelection(ticket);

            if (this.openedSelection?.id === ticket.id) {
                this.setOpenedSelectionTo(ticket)
            }
        }

        private async expandIntoViewport(dataAttribute: string) {
            await PromiseHelper.awaitFor(300);
            const addToCartButton = document.querySelector('[data-js=submit-action]');
            const ticketContainer = document.querySelector(`[data-js=${dataAttribute}]`);

            if (!ticketContainer || !addToCartButton) {
                return;
            }

            const ticketContainerRect = ticketContainer.getBoundingClientRect();
            const addToCartButtonRect = addToCartButton.getBoundingClientRect();
            const relativePosition = window.innerHeight - ticketContainerRect.top - addToCartButtonRect.height;
            const panelContent = document.querySelector('[data-js=panel-content]');

            if (panelContent && relativePosition < ticketContainerRect.height) {
                panelContent.scrollBy({top: ticketContainerRect.height - relativePosition, behavior: 'smooth'});
            }
        }

        protected async created() {
            this.currencyHandler = new CurrencyHandler(
                this.getCurrencyFrom(this.data) ?? '',
                this.data.currencyOptions?.currency ?? '',
                this.data.currencyOptions?.conversionRate ?? '1',
                this.data.currencyOptions?.showDoubleCurrency ?? false,
            );

            this.submitButtonLabel = await this.$translationService
                .getTranslationFor(this.Translation.Ticket_selection_ticket_count_submit);
        }
    }
</script>


<template>
    <div v-bind:class="{'-is-fullHeight' : !isMobile}"
         class="o-seatmapSelection__list clearfix" >
        <PromotionPanel v-if="promotionStore.availablePromotions.length"
                        :event-id="data.configuration.eventId"
                        v-bind:class="{'padding-top': isMobile}"
                        class="o-seatmapSelection__promoPanel">
        </PromotionPanel>

        <div v-if="!seatmapStore.getStructuredSelection || seatmapStore.getStructuredSelection.length === 0"
             v-translate="{ key: Translation.Seatmap_select_tickets }"
             class="o-seatmapSelection__noSelectionNotice">
            Select your tickets by clicking on available seats or standing area
        </div>

        <template v-if="seatmapStore.hasMismatchedCurrencies">
            <div class="o-seatmapSelection__notice">
                <p>Event configuration is invalid - selected price levels have different currencies!</p>
            </div>
        </template>

        <div v-if="seatmapStore.getStructuredSelection?.length > 0" class="o-seatmapSelection__priceLevelsContainer">
            <template v-for="(priceLevel, pricelevelIndex) in seatmapStore.getStructuredSelection"
                      :key="pricelevelIndex">
                <div v-bind:data-js="'pricelevel-' + pricelevelIndex"
                     v-bind:class="{
                        '-topMargin': !isMobile && !panelState.isPromotionPanelActive() && !promotionStore.active
                     }"
                     :data-qa="PerformanceQASelectors.PRICE_LEVEL_TICKET"
                     class="o-seatmapSelection__priceLevel">
                    <div class="o-seatmapSelection__priceLevelHeader">
                        <div class="o-seatmapSelection__priceLevelTitle" :data-qa="PerformanceQASelectors.PRICE_LEVEL_TITLE">
                            <span v-bind:style="{ backgroundColor: priceLevel.selectionInfo.color }"
                                class="a-coloredCircle" ></span>
                            {{ priceLevel.selectionInfo.priceCategoryName }}
                        </div>

                        <div class="o-seatmapSelection__ticketAction">
                            <icon-component :icon-id="IconName.Cross"
                                v-on:click="removeSelection(priceLevel.selectionInfo)"/>
                        </div>
                    </div>

                    <div class="o-seatmapSelection__priceLevelDescription">
                        {{ priceLevel.selectionInfo.description }}
                    </div>

                    <template v-for="(priceCategoryError, index) in seatmapStore.validation.priceCategories"
                        :key="index">
                        <div v-if="priceCategoryError.id === priceLevel.selectionInfo.priceCategoryId"
                            class="o-seatmapSelection__priceLevelNotice">
                            <p v-if="priceCategoryError.max"
                               v-translate="{
                                   key: Translation.Seatmap_max_price_level_restriction_error,
                                   keyValues: JSON.stringify({
                                   'placeholder-price-category-name': priceLevel.selectionInfo.name,
                                   'placeholder-max-amount': priceLevel.selectionInfo.maxTickets }),
                                   identifierStart: '{',
                                   identifierEnd: '}',
                                   }"
                               class="a-notice -error">
                                You can only add {{ priceLevel.selectionInfo.maxTickets }} to selection from
                                {{ priceLevel.selectionInfo.name }}</p>
                        </div>
                    </template>

                    <div class="o-seatmapSelection__ticketList">
                        <div v-if="priceLevel.selectionInfo"
                            class="o-seatmapSelection__ticketContainer">
                            <div class="o-seatmapSelection__ticket">
                                <div class="o-seatmapSelection__ticketWrapper">
                                    <div class="o-seatmapSelection__ticketInformation"
                                         :data-qa="PerformanceQASelectors.TICKET_INFORMATION">
                                        <template v-if="priceLevel.selectionInfo.seat && priceLevel.selectionInfo.row">
                                            <div>
                                                <span v-translate="{ key: Translation.Row_label }">Row </span>
                                                <span>: {{ priceLevel.selectionInfo.row }}</span>
                                            </div>
                                            <div>
                                                <span v-translate="{ key: Translation.Seat_label }">Seat </span>
                                                <span>: {{ priceLevel.selectionInfo.seat }}</span>
                                            </div>
                                        </template>

                                        <template v-else>
                                            <div v-if="priceLevel.selectionInfo.entranceName">
                                                <span v-translate="{ key: Translation.Entrance_label }">Entrance</span>
                                                <span>: {{ priceLevel.selectionInfo.entranceName }}</span>
                                            </div>
                                            <div v-if="priceLevel.selectionInfo.blockName">
                                                <span>Sector</span>
                                                <span>: {{ priceLevel.selectionInfo.blockName }}</span>
                                            </div>
                                        </template>
                                    </div>
                                </div>
                            </div>

                            <div v-bind:class="{'-changedMargin': priceLevel.selectionInfo.ticketTypes.length === 1}"
                                class="o-seatmapSelection__ticketType"
                                :data-qa="PerformanceQASelectors.TICKET_TYPE">
                                <template v-if="priceLevel.selectionInfo.ticketTypes.length === 1">
                                    <div class="o-seatmapSelection__ticketOption bottom-padding">
                                        <p class="o-seatmapSelection__ticketName">
                                            {{ priceLevel.selectionInfo.ticketTypes[0].name }}</p>
                                        <div class="o-seatmapSelection__priceItems">
                                            <p class="o-seatmapSelection__pricePrimary">
                                                {{ currencyHandler.displayPrimaryCurrency(
                                                        priceLevel.selectionInfo.ticketTypes[0].price) }}</p>
                                            <p v-if="currencyHandler.isSecondaryCurrencyDisplayed"
                                                   class="o-seatmapSelection__pricePrimary">
                                                /{{ currencyHandler.displaySecondaryCurrency(
                                                        priceLevel.selectionInfo.ticketTypes[0].price) }}
                                            </p>
                                        </div>
                                    </div>
                                </template>

                                <template v-else>
                                    <div class="o-seatmapSelection__ticketTypeContainer">
                                        <div v-bind:class="{'-has-border' : isTicketOpen(priceLevel.selectionInfo)}"
                                             class="o-seatmapSelection__typeSelection"
                                             v-on:click="setOpenedSelectionTo(priceLevel.selectionInfo, pricelevelIndex)">
                                            <p class="o-seatmapSelection__ticketName">
                                                {{ getActiveTicketTypeFor(priceLevel.selectionInfo).name }}</p>
                                            <div class="o-seatmapSelection__dropdownContent">
                                                <div class="o-seatmapSelection__priceItems padding-right">
                                                    <p class="o-seatmapSelection__pricePrimary">
                                                        {{ currencyHandler.displayPrimaryCurrency(
                                                                getActiveTicketTypeFor(priceLevel.selectionInfo).price)
                                                        }}</p>
                                                    <p v-if="currencyHandler.isSecondaryCurrencyDisplayed"
                                                        class="o-seatmapSelection__pricePrimary">
                                                        / {{ currencyHandler.displaySecondaryCurrency(
                                                                getActiveTicketTypeFor(priceLevel.selectionInfo).price)
                                                        }}
                                                    </p>
                                                </div>
                                                <IconComponent :icon-id="IconName.Arrow"
                                                    v-bind:class="isTicketOpen(priceLevel.selectionInfo) ?
                                                        'o-seatmapSelection__collapseArrow'
                                                        : 'o-seatmapSelection__expandArrow'"></IconComponent>
                                            </div>
                                        </div>
                                        <transition-group name="slide">
                                            <div v-for="(ticketType, index) in priceLevel.selectionInfo.ticketTypes"
                                                 v-show="openedSelection && isTicketOpen(priceLevel.selectionInfo)"
                                                 v-bind:class="{ '-active': ticketType.active }" v-bind:key="index"
                                                 class="o-seatmapSelection__typeSelection"
                                                 v-on:click="handleTicketTypeSelection(priceLevel.selectionInfo, ticketType)">
                                                <p class="o-seatmapSelection__ticketName"> {{ ticketType.name }} </p>
                                                <div class="o-seatmapSelection__priceItems">
                                                    <p class="o-seatmapSelection__pricePrimary">
                                                        {{ currencyHandler.displayPrimaryCurrency(ticketType.price) }}</p>
                                                    <p v-if="currencyHandler.isSecondaryCurrencyDisplayed"
                                                        class="o-seatmapSelection__pricePrimary">
                                                        /{{ currencyHandler.displaySecondaryCurrency(ticketType.price) }}
                                                    </p>
                                                </div>
                                            </div>
                                        </transition-group>
                                    </div>
                                </template>

                                <template
                                    v-for="activeTicketType in priceLevel.selectionInfo.ticketTypes.filter(
                                        item => item.active)">
                                    <template
                                        v-for="(ticketTypeError, index) in seatmapStore.validation.ticketTypes"
                                        :key="index">
                                        <div v-if="ticketTypeError.id === activeTicketType.priceId &&
                                            !ticketTypeError.max">
                                            <p v-if="ticketTypeError.min"
                                               v-translate="{
                                                           key: Translation.Seatmap_min_ticket_restriction_error,
                                                           keyValues: JSON.stringify({
                                                           'placeholder-min-value': activeTicketType.min,
                                                           'placeholder-rebate-name': activeTicketType.name,
                                                           'placeholder-price-category-name':
                                                               priceLevel.selectionInfo.name }),
                                                           identifierStart: '{',
                                                           identifierEnd: '}',
                                                           }"
                                               class="a-notice -error">
                                                Minimum amount restriction not reached</p>

                                            <p v-if="ticketTypeError.max"
                                               class="a-notice -error"
                                               v-translate="{
                                                           key: Translation.Seatmap_max_ticket_restriction_error,
                                                           keyValues: JSON.stringify({
                                                           'placeholder-rebate-name': activeTicketType.name,
                                                           'placeholder-price-category-name':
                                                               priceLevel.selectionInfo.name }),
                                                           identifierStart: '{',
                                                           identifierEnd: '}',
                                                           }">
                                                Max amount restriction exceeded</p>

                                            <p v-if="ticketTypeError.step"
                                                class="a-notice -error"
                                                v-translate="{
                                                    key: Translation.Seatmap_step_ticket_restriction_error,
                                                    keyValues: JSON.stringify({
                                                        'ticket-type-name': activeTicketType.name,
                                                        'step-restriction-amount': activeTicketType.step }),
                                                    identifierStart: '{',
                                                    identifierEnd: '}',
                                                }">Ticket grouping restriction not satisfied</p>
                                        </div>
                                    </template>
                                </template>
                            </div>
                        </div>

                    </div>
                </div>
            </template>
        </div>

        <div data-js="submit-action" :data-qa="PerformanceQASelectors.SUBMIT_BUTTON" class="o-seatmapSelection__submitAction">
            <button-component
                :label="submitSelectionLabel"
                :active="seatmapStore.validation.isValid"
                :icon-name="IconName.Cart"
                :secondary-label="currencyHandler.displaySecondaryCurrency(seatmapStore.totalSelectionAmount)"
                v-on:submit="panelState.triggerAddToCart()"
                :qa-selector="PerformanceQASelectors.PRICE_LEVEL_SUBMIT"
            />
            <TicketFeeInfo :currency-handler="currencyHandler"
                           :fee-list="seatmapStore.fees"
                           :ticket-price-list="getSelectedTicketsPriceList()"
                           :has-opacity="totalTicketsSelected === 0">
            </TicketFeeInfo>
        </div>
    </div>
</template>

<style scoped>
    .multiselect--above .multiselect__content-wrapper {
        border-radius: var(--border-radius) var(--border-radius) 0 0;
    }
</style>